import React, { useRef } from 'react'
import styled from 'styled-components'
import { skills } from '../../data/constants'
import * as SiIcons from 'react-icons/si';
import * as FaIcons from 'react-icons/fa';

const iconColors = {
  SiJavascript: "#F7DF1E",
  SiTypescript: "#3178C6",
  SiPython: "#3776AB",
  SiC: "#A8B9CC",
  SiCplusplus: "#00599C",
  SiGnubash: "#4EAA25",
  SiMysql: "#4479A1",
  FaReact: "#61DAFB",
  SiNextdotjs: "#999",
  FaNodeJs: "#339933",
  SiExpress: "#999",
  SiTailwindcss: "#06B6D4",
  SiMaterialui: "#007FFF",
  SiRedux: "#764ABC",
  FaDocker: "#2496ED",
  SiKubernetes: "#326CE5",
  SiHelm: "#277A9F",
  SiJenkins: "#D24939",
  SiGit: "#F05032",
  FaGithub: "#999",
  FaGitlab: "#FC6D26",
  SiAnsible: "#EE0000",
  SiArgocd: "#EF7B4D",
  SiSonarqube: "#4E9BCD",
  SiTerraform: "#623CE4",
  FaAws: "#FF9900",
  FaMicrosoft: "#0078D4",
  SiGooglecloud: "#4285F4",
  SiMongodb: "#47A248",
  SiPostgresql: "#336791",
  SiRedis: "#DC382D",
  SiPrometheus: "#E6522C",
  SiGrafana: "#F46800",
  SiGithubactions: "#2088FF",
  FaLinux: "#FCC624",
  FaWindows: "#0078D6",
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  z-index: 1;
  align-items: center;
  width: 100%;
  padding: 40px 0 80px;
`

const Wrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  width: 100%;
  max-width: 1100px;
  gap: 12px;
  padding: 0 16px;
`

const Title = styled.h2`
  font-size: 42px;
  text-align: center;
  font-weight: 700;
  margin-top: 20px;
  color: ${({ theme }) => theme.text_primary};
  letter-spacing: -0.5px;

  @media (max-width: 768px) {
    margin-top: 12px;
    font-size: 32px;
  }
`;

const Desc = styled.div`
  font-size: 17px;
  text-align: center;
  max-width: 600px;
  color: ${({ theme }) => theme.text_secondary};
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const SkillsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  margin-top: 30px;
  gap: 24px;
  justify-content: center;
`

const SkillCard = styled.div`
  width: 100%;
  max-width: 500px;
  background: ${({ theme }) => theme.card};
  border: 1px solid ${({ theme }) => theme.card_border};
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-radius: 16px;
  padding: 18px 36px;
  position: relative;
  transition: all 0.3s cubic-bezier(0.23, 1, 0.32, 1);
  will-change: transform;
  overflow: visible;

  @media (max-width: 768px) {
    max-width: 400px;
    padding: 14px 28px;
  }
  @media (max-width: 500px) {
    max-width: 100%;
    padding: 14px 20px;
  }

  &:hover {
    border-color: ${({ theme }) => theme.primary};
    box-shadow: 0 4px 32px ${({ theme }) => theme.shadow_primary},
                0 0 0 1px ${({ theme }) => theme.primary}33;
  }
`

const SkillTitle = styled.h3`
  font-size: 24px;
  font-weight: 600;
  color: ${({ theme }) => theme.text_primary};
  margin-bottom: 18px;
  text-align: center;
  letter-spacing: -0.3px;
`

const SkillList = styled.div`
  display: flex;
  justify-content: center; 
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
`

const SkillItem = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.text_secondary};
  border: 1px solid ${({ theme }) => theme.divider};
  border-radius: 10px;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.25s ease;
  background: transparent;

  &:hover {
    border-color: ${({ theme }) => theme.primary}55;
    background: ${({ theme }) => theme.primary_light};
    color: ${({ theme }) => theme.text_primary};
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    font-size: 13px;
    padding: 8px 12px;
  }
`

const Skill = ({ children }) => {
  const cardRef = useRef();

  const handleMouseMove = (e) => {
    if (window.innerWidth <= 768) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = ((y - centerY) / centerY) * 6;
    const rotateY = ((x - centerX) / centerX) * 10;
    cardRef.current.style.transform = `perspective(700px) rotateX(${-rotateX}deg) rotateY(${rotateY}deg) scale(1.02)`;
  };

  const handleMouseLeave = () => {
    if (window.innerWidth <= 768) return;
    cardRef.current.style.transform = 'perspective(700px) rotateX(0deg) rotateY(0deg) scale(1)';
  };

  return (
    <SkillCard
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {children}
    </SkillCard>
  );
};

const Skills = () => {
  const getIconComponent = (iconName) => {
    if (!iconName) return null;
    if (iconName.startsWith('Si')) return SiIcons[iconName];
    if (iconName.startsWith('Fa')) return FaIcons[iconName];
    return null;
  };

  return (
    <Container id="skills">
      <Wrapper>
        <Title>Skills</Title>
        <Desc>Technologies and tools I work with across the full stack, DevOps, and cloud.</Desc>
        <SkillsContainer>
          {skills.map((skill) => (
            <Skill key={skill.title}>
              <SkillTitle>{skill.title}</SkillTitle>
              <SkillList>
                {skill.skills.map((item) => {
                  const Icon = getIconComponent(item.icon);
                  return (
                    <SkillItem key={item.name}>
                      {Icon && <Icon size={20} color={iconColors[item.icon]} />}
                      {item.name}
                    </SkillItem>
                  );
                })}
              </SkillList>
            </Skill>
          ))}
        </SkillsContainer>
      </Wrapper>
    </Container>
  )
}

export default Skills
